/**
 * The employee (trabajador) and identity-document-type resources.
 *
 * Field names mirror `apps/employees/serializers.py` exactly. Names, email and
 * username live on the linked `User` — creating an employee creates its
 * account too, and the response carries the one-time `temporary_password`.
 */
import { apiClient } from '@/api/client';
import { createApiResource } from '@/api/resource';
import type { Activatable, BaseRecord, ListParams, Paginated } from '@/api/types';

export type EmploymentStatus = 'active' | 'on_leave' | 'suspended' | 'terminated';

export type EmployeeType = 'interno' | 'externo';

/** Formatos de `/employees/report/{format}/` — el padrón descargable. */
export type EmployeeReportFormat = 'xlsx' | 'pdf';

/**
 * A document attached to the worker's expediente — the generic file resource
 * (`EmployeeFile`), the one behind the profile photo. Every file type lives in a
 * catalog, so this record identifies its type by `file_type_code` (a stable
 * business code) and not by the PK: `PROFILE_PHOTO` is the only one the UI
 * renders, and RR.HH. can add more without a deploy.
 */
export interface EmployeeFile extends BaseRecord {
  /** EmployeeFileType PK — what to send on upload. */
  file_type: string;
  /** Stable catalog code (`PROFILE_PHOTO`, `CONADIS`, ...) — what to branch on. */
  file_type_code: string | null;
  file_type_name: string | null;
  /** The audit note (RN-25) the file was attached with. */
  note: string;
  original_filename: string;
  content_type: string;
  size_bytes: number | null;
  storage_key: string;
  is_active: boolean;
}

/** TextChoices.Sex — kept as a union, not a catalog, because payroll branches on it. */
export type Sex = 'female' | 'male';

/** TextChoices.MaritalStatus. */
export type MaritalStatus = 'single' | 'married' | 'divorced' | 'widowed' | 'cohabiting';

/** TextChoices.Currency — PEN soles, USD dólares. */
export type Currency = 'PEN' | 'USD';

/** TextChoices.PaymentForm — depósito a cuenta sueldo o pago en efectivo. */
export type PaymentForm = 'bank_deposit' | 'cash';

export interface FunctionalManager {
  full_name: string;
  position_name: string;
}

/**
 * The backend's single reading of a history row: `voided` (uploaded by
 * mistake, annulled with a reason) beats everything, then `terminated` (the
 * placement a cese closed); among the rest, the period decides. `ended` covers
 * both a row closed by a transfer and one that ran past its end date.
 */
export type AssignmentStatus = 'current' | 'scheduled' | 'ended' | 'voided' | 'terminated';

export interface EmployeeAssignment {
  id: string;
  site_position: string;
  /** Site PK of the placement — lets the edit dialog pre-filter the position dropdown. */
  site: string;
  /** Área (Department) of the seat — pre-fills the unit select on edit. */
  unit: string;
  unit_name: string;
  /** What the person actually does when the cargo is generic («Camarógrafo»). */
  function: string;
  site_position_display: string;
  manager_name: string | null;
  /** Dotted-line (FUNCIONAL) bosses of the placement, name + cargo. */
  functional_managers: FunctionalManager[];
  /** Régimen laboral PK — the name comes from the catalog by joining on it. */
  employment_regime: string | null;
  /** Remuneración mensual — the backend Decimal reaches the wire as a string. */
  remuneration: string | null;
  payment_form: PaymentForm | null;
  /**
   * Display columns for the assignment history table, computed by the backend
   * serializer (nullable FKs serialized as null rather than dropped).
   */
  site_code: string | null;
  position_code: string | null;
  position_name: string | null;
  employment_regime_name: string | null;
  /**
   * Contract file columns. The backend treats an empty `storage_key` as "no
   * hay contrato" and the file as read-only: only ContractFileService writes
   * them through upload/discard.
   */
  storage_key: string;
  original_filename: string;
  content_type: string;
  size_bytes: number | null;
  effective_from: string;
  effective_to: string | null;
  is_active: boolean;
  is_current: boolean;
  status: AssignmentStatus;
  voided_at: string | null;
  voided_by_name: string | null;
  void_reason: string;
  /**
   * The cese this placement closed, if any — `effective_to` is its date. Kept
   * for a future re-hire: why the person left and whether HR takes them back.
   */
  termination_reason: string | null;
  termination_reason_name: string | null;
  termination_notes: string;
  rehire_eligible: boolean | null;
  reason: string;
}

/** Input of `POST /employees/{id}/terminate/`. `reason` is the RN-25 text. */
export interface TerminationInput {
  termination_date: string;
  termination_reason: string;
  rehire_eligible: boolean;
  reason: string;
}

/** The placement of the worker's last cese, newest first; null if never ceased. */
export function getLastTermination(employee: Employee): EmployeeAssignment | null {
  return employee.employment_assignments.find((a) => a.status === 'terminated') ?? null;
}

/** Split `site_position_display` ("Cajero (demo) · Huánuco (demo)") into parts. */
export function parseSitePositionDisplay(display: string): {
  positionName: string;
  siteName: string;
} {
  const sep = ' · ';
  const idx = display.indexOf(sep);
  if (idx === -1) return { positionName: display, siteName: '' };
  return { positionName: display.slice(0, idx), siteName: display.slice(idx + sep.length) };
}

/**
 * Return the assignment in force today, or null.
 *
 * A transfer leaves two `is_active` rows (the closed one keeps its history),
 * so `is_current` is what separates the live placement from the archived one.
 * The bare-`is_active` fallback covers data predating that convention. A
 * ceased row also stays `is_active` (it keeps HR's site scope) but it is no
 * longer the worker's contract, so it never answers here.
 */
export function getActiveAssignment(employee: Employee): EmployeeAssignment | null {
  const list = employee.employment_assignments.filter((a) => a.status !== 'terminated');
  return list.find((a) => a.is_active && a.is_current) ?? list.find((a) => a.is_active) ?? null;
}

/** Convenience: parsed site name from the active assignment. */
export function getSiteName(employee: Employee): string | null {
  const a = getActiveAssignment(employee);
  if (!a) return null;
  return parseSitePositionDisplay(a.site_position_display).siteName || null;
}

/** Convenience: parsed position name from the active assignment. */
export function getPositionName(employee: Employee): string | null {
  const a = getActiveAssignment(employee);
  if (!a) return null;
  return parseSitePositionDisplay(a.site_position_display).positionName || null;
}

/** Convenience: manager name from the active assignment. */
export function getManagerName(employee: Employee): string | null {
  const a = getActiveAssignment(employee);
  return a?.manager_name ?? null;
}

/** The backend's own rule: empty `storage_key` means no contract attached. */
export function hasContract(assignment: EmployeeAssignment): boolean {
  return assignment.storage_key !== '';
}

/**
 * The same empty-key convention for every ficha record that carries a file
 * (family dependents, academic backgrounds): no `storage_key` IS "sin
 * documento". No endpoint resolves these files yet, so the placeholder render
 * will grow a download once the ficha-file upload service lands.
 */
export function hasAttachedFile(record: { storage_key: string }): boolean {
  return record.storage_key !== '';
}

/** Convenience: functional managers (name + cargo) from the active assignment. */
export function getFunctionalManagers(employee: Employee): FunctionalManager[] {
  return getActiveAssignment(employee)?.functional_managers ?? [];
}

export interface IdentityDocumentType extends BaseRecord, Activatable {
  code: string;
  name: string;
  validator_key: string;
  sort_order: number;
}

export interface Employee extends BaseRecord, Activatable {
  code: string;
  user: string;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  mother_last_name: string;
  full_name: string;
  document_type: string;
  document_type_name: string;
  document_number: string;
  phone: string;
  /** Personal and payroll references (null until backfilled — nullable FKs). */
  sex: Sex | null;
  nationality: string | null;
  nationality_name: string | null;
  marital_status: MaritalStatus | null;
  home_address: string | null;
  /** UBIGEO district PK; the cascade derives department/province from its digits. */
  district: string | null;
  district_name: string | null;
  bank: string | null;
  bank_name: string | null;
  bank_account_number: string | null;
  salary_currency: Currency | null;
  cts_bank: string | null;
  cts_bank_name: string | null;
  cts_account_number: string | null;
  cts_currency: Currency | null;
  /** null until answered; the form sends the explicit boolean once chosen. */
  has_children: boolean | null;
  type_employee: EmployeeType;
  date_of_birth: string | null;
  /** Active placement's SitePosition PK — echoed for the edit form. */
  site: string | null;
  /** The Site itself, to pre-filter the position dropdown when editing. */
  site_id: string | null;
  /** Área of the active placement's seat, to pre-filter the unit select. */
  unit_id: string | null;
  /** Función of the active placement; '' when none. */
  function: string | null;
  site_name: string;
  department: string;
  department_name: string;
  position: string;
  position_name: string;
  manager: string | null;
  manager_name: string | null;
  hired_on: string;
  last_position_changed_on: string | null;
  status: EmploymentStatus;
  /** Real account state (User.is_active) — can diverge from `is_active`. */
  account_active: boolean;
  /**
   * The worker's current photo, already inlined on the detail response so the
   * avatar does not need a second round trip. `null` when there is none.
   */
  profile_photo: EmployeeFile | null;
  employment_assignments: EmployeeAssignment[];
  /** Served only on create — the one-time temporary password. */
  temporary_password?: string | undefined;
}

export interface EmployeeInput {
  email: string;
  first_name: string;
  last_name: string;
  mother_last_name?: string;
  document_type: string;
  document_number: string;
  phone?: string;
  sex?: Sex;
  /** Catalog PKs — sent only when the select has a value. */
  nationality?: string;
  marital_status?: MaritalStatus;
  home_address?: string;
  district?: string;
  /** `null` clears the FK on PATCH; create omits it when there is no bank. */
  bank?: string | null;
  bank_account_number?: string;
  /** `''` clears the stored currency on PATCH. */
  salary_currency?: Currency | '';
  cts_bank?: string | null;
  cts_account_number?: string;
  cts_currency?: Currency | '';
  has_children?: boolean;
  type_employee: EmployeeType;
  date_of_birth?: string | undefined;
  /** SitePosition PK of the placement — optional for EXTERNO workers. */
  site?: string | undefined;
  /** Función on the placement; travels with `site`. */
  function?: string;
  /** Contract data of the current placement — travel alongside `site`. */
  employment_regime?: string;
  remuneration?: string;
  payment_form?: PaymentForm;
  hired_on?: string;
  /** Fecha de cese de la asignación inicial — solo INTERNO, viaja con `site`. */
  effective_to?: string;
}

/**
 * Una fila del listado ligero (`/employees/table/`): el subset que la grilla
 * pinta, sin la historia de asignaciones ni los jefes. Las fechas son las de la
 * asignación *vigente*; un trabajador sin contrato en curso expone nulls.
 */
export interface EmployeeTableRow {
  id: string;
  type_employee: EmployeeType;
  full_name: string;
  document_type_name: string;
  document_number: string;
  /**
   * Del contrato activo, sin mirar sus fechas: uno vencido sin cese o que aún
   * no empieza sigue siendo el que el trabajador tiene. Null cuando no hay
   * ninguno, sea interno o externo.
   */
  site_name: string | null;
  position_name: string | null;
  status: EmploymentStatus;
  effective_from: string | null;
  effective_to: string | null;
  /** Máximo `updated_at` entre la ficha y sus registros relacionados. */
  last_updated_at: string;
  /** Contrato vigente que termina dentro de `contract.expiry_warning_days`. */
  contract_ending_soon: boolean;
}

/**
 * A new placement for the worker — essentially a transfer (RF-040): the
 * backend closes the current assignment and opens this one, audited and
 * atomic. `site_position` is the only required field; the rest are the
 * placement's own contract data and can be left empty to fill later.
 */
export interface EmploymentAssignmentInput {
  /** SitePosition PK of the new placement. */
  site_position: string;
  effective_from?: string;
  /** `null` clears the end date (indefinido) on PATCH. */
  effective_to?: string | null;
  employment_regime?: string;
  remuneration?: string;
  payment_form?: PaymentForm;
  /** Función; '' clears it. */
  function?: string;
  reason?: string;
}

/**
 * Carga familiar of the worker. `kinship_name` is read-only (the table joins
 * on the catalog); the client writes the PK (`kinship`). The four file columns
 * are read-only: upload/discard will come with a dedicated service, never here.
 */
export interface FamilyDependent extends BaseRecord, Activatable {
  full_name: string;
  kinship: string;
  kinship_name: string | null;
  document_number: string;
  birth_date: string | null;
  sex: Sex | null;
  storage_key: string;
  original_filename: string;
  content_type: string;
  size_bytes: number | null;
}

export interface FamilyDependentInput {
  full_name: string;
  kinship: string;
  document_number?: string;
  birth_date?: string | undefined;
  sex?: Sex;
}

/**
 * Formación académica of the worker. `education_level_name` is read-only; the
 * client writes the catalog PK. The file columns follow the same read-only
 * rule as `FamilyDependent`.
 */
export interface AcademicBackground extends BaseRecord, Activatable {
  education_level: string;
  education_level_name: string | null;
  institution_name: string;
  career_specialty: string;
  graduation_year: number | null;
  licensure_number: string;
  storage_key: string;
  original_filename: string;
  content_type: string;
  size_bytes: number | null;
}

export interface AcademicBackgroundInput {
  education_level: string;
  institution_name: string;
  career_specialty?: string;
  /** `null` clears the field on PATCH — the backend column is nullable. */
  graduation_year?: number | null;
  licensure_number?: string;
}

/** TextChoices.SituacionProvisional of the pension system. */
export type SituacionProvisional = 'activo_afp' | 'permanecer_snp' | 'sin_afiliacion';

/** TextChoices.PensionRegime — AFP or SNP (ONP). */
export type PensionRegime = 'afp' | 'onp';

/** TextChoices.CommissionType of the AFP. */
export type CommissionType = 'flujo_sueldo' | 'no_aplica' | 'mixta_saldo';

/** TextChoices.FundType of the AFP. */
export type FundType = 'no_aplica' | 'fondo_mixto';

/**
 * Sistema de pensiones — the worker's single pension record (1-a-1). The label
 * fields are read-only (the table joins on the catalog/TextChoices); the client
 * writes the raw `TextChoices` values and the `afp` catalog PK. The four file
 * columns follow the same read-only rule as `FamilyDependent`: upload/download
 * will come with a dedicated service, never here.
 */
export interface PensionSystem extends BaseRecord, Activatable {
  situacion_provisional: SituacionProvisional;
  situacion_provisional_label: string | null;
  regimen_afiliado: PensionRegime;
  regimen_afiliado_label: string | null;
  afp: string | null;
  afp_name: string | null;
  cuspp: string;
  tipo_comision: CommissionType;
  tipo_comision_label: string | null;
  tipo_fondo: FundType;
  tipo_fondo_label: string | null;
  recibio_boletin: boolean | null;
  fecha_entrega_boletin: string | null;
  fecha_limite_eleccion: string | null;
  fecha_firma_declaracion: string | null;
  storage_key: string;
  original_filename: string;
  content_type: string;
  size_bytes: number | null;
}

export interface PensionSystemInput {
  situacion_provisional?: SituacionProvisional | '';
  regimen_afiliado?: PensionRegime | '';
  afp?: string | null;
  cuspp?: string;
  tipo_comision?: CommissionType | '';
  tipo_fondo?: FundType | '';
  recibio_boletin?: boolean | null;
  fecha_entrega_boletin?: string | null;
  fecha_limite_eleccion?: string | null;
  fecha_firma_declaracion?: string | null;
}

/**
 * One printable worker of `/employees/id-badges/`: exactly what the fotocheck
 * prints. `photo_url` comes already signed (short-lived), so a print run does
 * not pay one request per photo; `document_type_code` is the catalog code
 * (`DNI`), never the name.
 */
export interface IdBadgeRow {
  id: string;
  code: string;
  full_name: string;
  document_type_code: string | null;
  document_number: string;
  position_name: string | null;
  type_employee: EmployeeType;
  photo_url: string | null;
}

export const employeesApi = {
  ...createApiResource<Employee, EmployeeInput>('/api/v1/employees/'),
  /**
   * Cese: closes the placement, sets the status to `terminated` and revokes
   * access, in one transaction. There is no DELETE on employees any more.
   */
  async terminate(id: string, payload: TerminationInput): Promise<Employee> {
    const { data } = await apiClient.post<Employee>(`/api/v1/employees/${id}/terminate/`, payload);
    return data;
  },

  /** La propia ficha — no requiere el permiso de listado. */
  async me(): Promise<Employee> {
    const { data } = await apiClient.get<Employee>('/api/v1/employees/me/');
    return data;
  },

  /**
   * Las filas del listado ligero. Mismos filtros/orden que el listado completo
   * (el buscador sigue usando `/employees/`), pero el payload es solo lo que la
   * grilla pinta.
   */
  /** Active workers to print, with their photo already signed. Every page audits its photos. */
  async getIdBadges(params: ListParams): Promise<Paginated<IdBadgeRow>> {
    const { data } = await apiClient.get<Paginated<IdBadgeRow>>('/api/v1/employees/id-badges/', {
      params,
    });
    return data;
  },

  async getTable(params: ListParams): Promise<Paginated<EmployeeTableRow>> {
    const { data } = await apiClient.get<Paginated<EmployeeTableRow>>('/api/v1/employees/table/', {
      params,
    });
    return data;
  },

  /**
   * Padrón de internos activos (Excel o PDF) con los mismos filtros del
   * listado. El backend fija el orden y el nombre del archivo; aquí solo se
   * dispara la descarga.
   */
  async downloadReport(format: EmployeeReportFormat, params: ListParams): Promise<void> {
    const { page: _page, page_size: _pageSize, ordering: _ordering, ...filters } = params;
    const response = await apiClient.get<Blob>(`/api/v1/employees/report/${format}/`, {
      params: filters,
      responseType: 'blob',
    });
    const disposition = String(response.headers['content-disposition'] ?? '');
    // Sin CORS_EXPOSE_HEADERS el navegador oculta el header en otro origen.
    const fileName = /filename="([^"]+)"/.exec(disposition)?.[1] ?? `padron_trabajadores.${format}`;
    const url = URL.createObjectURL(response.data);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  },

  /**
   * Cuántos contratos vigentes terminan dentro de la ventana configurada
   * (`contract.expiry_warning_days`). Alimenta la tarjeta de aviso sobre el
   * listado; el backend lo calcula con el mismo predicado que enciende el
   * amarillo de la tabla.
   */
  async getContractsExpiring(): Promise<{ count: number }> {
    const { data } = await apiClient.get<{ count: number }>(
      '/api/v1/employees/contracts-expiring/',
    );
    return data;
  },

  /**
   * The person's full assignment history, newest first (`-effective_from` is
   * the backend's default ordering). Nested under the employee, so the router
   * scopes it to the ficha and no filter is needed.
   */
  async listAssignments(
    employeeId: string,
    params: ListParams,
  ): Promise<Paginated<EmployeeAssignment>> {
    const { data } = await apiClient.get<Paginated<EmployeeAssignment>>(
      `/api/v1/employees/${employeeId}/assignments/`,
      { params },
    );
    return data;
  },

  /**
   * Creates a new placement (a transfer under the hood, see
   * `EmploymentAssignmentInput`). The file, when chosen, goes through
   * `uploadContract` right after, never as part of this JSON body.
   */
  async createAssignment(
    employeeId: string,
    payload: EmploymentAssignmentInput,
  ): Promise<EmployeeAssignment> {
    const { data } = await apiClient.post<EmployeeAssignment>(
      `/api/v1/employees/${employeeId}/assignments/`,
      payload,
    );
    return data;
  },

  /**
   * Edits an existing assignment row in place (PATCH). Changing the placement
   * of the *current* row also moves the employee's scope on the backend
   * (RN-24), so the same payload shape as create is accepted. The reason is
   * mandatory on edit for the audit trail (RN-25).
   */
  async updateAssignment(
    employeeId: string,
    assignmentId: string,
    payload: EmploymentAssignmentInput,
  ): Promise<EmployeeAssignment> {
    const { data } = await apiClient.patch<EmployeeAssignment>(
      `/api/v1/employees/${employeeId}/assignments/${assignmentId}/`,
      payload,
    );
    return data;
  },

  /**
   * Annuls a placement uploaded by mistake. Nothing is deleted: the row stays
   * in the history marked as voided, with who and why (RN-25). It is final —
   * the right placement is registered anew — and, when it undoes a wrong
   * transfer, the backend reopens the contract that transfer closed.
   */
  async voidAssignment(
    employeeId: string,
    assignmentId: string,
    reason: string,
  ): Promise<EmployeeAssignment> {
    const { data } = await apiClient.post<EmployeeAssignment>(
      `/api/v1/employees/${employeeId}/assignments/${assignmentId}/void/`,
      { reason },
    );
    return data;
  },

  /**
   * The worker's family dependents, newest first (`-created_at` is the model's
   * default ordering). Nested under the employee like the assignments.
   */
  async listFamilyDependents(
    employeeId: string,
    params: ListParams,
  ): Promise<Paginated<FamilyDependent>> {
    const { data } = await apiClient.get<Paginated<FamilyDependent>>(
      `/api/v1/employees/${employeeId}/family-dependents/`,
      { params },
    );
    return data;
  },

  async createFamilyDependent(
    employeeId: string,
    payload: FamilyDependentInput,
  ): Promise<FamilyDependent> {
    const { data } = await apiClient.post<FamilyDependent>(
      `/api/v1/employees/${employeeId}/family-dependents/`,
      payload,
    );
    return data;
  },

  async updateFamilyDependent(
    employeeId: string,
    id: string,
    payload: FamilyDependentInput,
  ): Promise<FamilyDependent> {
    const { data } = await apiClient.patch<FamilyDependent>(
      `/api/v1/employees/${employeeId}/family-dependents/${id}/`,
      payload,
    );
    return data;
  },

  /**
   * Deactivates the dependent rather than destroying the row (RN-23). The
   * reason is mandatory to keep the audit trail telling *why* (RN-25), the
   * same contract as `createApiResource.remove`.
   */
  async removeFamilyDependent(employeeId: string, id: string, reason: string): Promise<void> {
    await apiClient.delete(`/api/v1/employees/${employeeId}/family-dependents/${id}/`, {
      data: { reason },
    });
  },

  /**
   * Sends the document file to the API, which validates and stores it,
   * answering with the finished record. A dependent that already carries a
   * document cannot be replaced (the backend rejects it): the row must be
   * deactivated and re-created to change its file.
   *
   * The `Content-Type` override stops `apiClient`'s default `application/json`
   * from stringifying the FormData into "[object File]".
   */
  async uploadFamilyDependentFile(
    employeeId: string,
    id: string,
    file: File,
  ): Promise<FamilyDependent> {
    const body = new FormData();
    body.append('file', file);
    const { data } = await apiClient.post<FamilyDependent>(
      `/api/v1/employees/${employeeId}/family-dependents/${id}/upload/`,
      body,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data;
  },

  /**
   * A short-lived presigned URL to read a dependent's document. Used by the
   * pop-up PDF viewer; the backend audits the read and gates it behind
   * `view_employee`.
   */
  async downloadFamilyDependentUrl(employeeId: string, id: string): Promise<string> {
    const { data } = await apiClient.post<{ url: string }>(
      `/api/v1/employees/${employeeId}/family-dependents/${id}/download/`,
    );
    return data.url;
  },

  /** The worker's academic backgrounds, newest first. Nested under the employee. */
  async listAcademicBackgrounds(
    employeeId: string,
    params: ListParams,
  ): Promise<Paginated<AcademicBackground>> {
    const { data } = await apiClient.get<Paginated<AcademicBackground>>(
      `/api/v1/employees/${employeeId}/academic-backgrounds/`,
      { params },
    );
    return data;
  },

  async createAcademicBackground(
    employeeId: string,
    payload: AcademicBackgroundInput,
  ): Promise<AcademicBackground> {
    const { data } = await apiClient.post<AcademicBackground>(
      `/api/v1/employees/${employeeId}/academic-backgrounds/`,
      payload,
    );
    return data;
  },

  async updateAcademicBackground(
    employeeId: string,
    id: string,
    payload: AcademicBackgroundInput,
  ): Promise<AcademicBackground> {
    const { data } = await apiClient.patch<AcademicBackground>(
      `/api/v1/employees/${employeeId}/academic-backgrounds/${id}/`,
      payload,
    );
    return data;
  },

  /**
   * Sends the certificate file to the API, which validates and stores it,
   * answering with the finished record. Replacing an existing file keeps the
   * old object in the store — clearing a document is a separate, later flow.
   *
   * The `Content-Type` override stops `apiClient`'s default `application/json`
   * from stringifying the FormData into "[object File]".
   */
  async uploadAcademicBackgroundFile(
    employeeId: string,
    id: string,
    file: File,
  ): Promise<AcademicBackground> {
    const body = new FormData();
    body.append('file', file);
    const { data } = await apiClient.post<AcademicBackground>(
      `/api/v1/employees/${employeeId}/academic-backgrounds/${id}/upload/`,
      body,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data;
  },

  /**
   * A short-lived presigned URL to read a certificate. Used by the pop-up PDF
   * viewer; the backend audits the read and gates it behind `view_employee`.
   */
  async downloadAcademicBackgroundUrl(employeeId: string, id: string): Promise<string> {
    const { data } = await apiClient.post<{ url: string }>(
      `/api/v1/employees/${employeeId}/academic-backgrounds/${id}/download/`,
    );
    return data.url;
  },

  /** Deactivates the background record (RN-23) with the audit reason (RN-25). */
  async removeAcademicBackground(employeeId: string, id: string, reason: string): Promise<void> {
    await apiClient.delete(`/api/v1/employees/${employeeId}/academic-backgrounds/${id}/`, {
      data: { reason },
    });
  },

  /**
   * The worker's pension system — a singleton 1-a-1 record. GET answers a 404
   * when the worker has none yet, which the section treats as "no registrado".
   */
  async getPension(employeeId: string): Promise<PensionSystem> {
    const { data } = await apiClient.get<PensionSystem>(`/api/v1/employees/${employeeId}/pension/`);
    return data;
  },

  /**
   * Saves the singleton via POST — the backend upserts, so a single call both
   * creates the first record and updates an existing one.
   */
  async savePension(employeeId: string, payload: PensionSystemInput): Promise<PensionSystem> {
    const { data } = await apiClient.post<PensionSystem>(
      `/api/v1/employees/${employeeId}/pension/`,
      payload,
    );
    return data;
  },

  /**
   * Sends the pension declaration file to the API, which validates it — type,
   * size, and that the bytes match the declared format — and writes it to the
   * store before answering with the finished record. Replacing an existing file
   * swaps the stored object; there is **no** discard (the declaration is only
   * ever replaced by another, never deleted).
   *
   * The `Content-Type` override stops `apiClient`'s default `application/json`
   * from stringifying the FormData, exactly like the other uploads.
   */
  async uploadPensionFile(employeeId: string, file: File): Promise<PensionSystem> {
    const body = new FormData();
    body.append('file', file);
    const { data } = await apiClient.post<PensionSystem>(
      `/api/v1/employees/${employeeId}/pension/upload/`,
      body,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data;
  },

  /**
   * A short-lived presigned URL to read the declaration. Used by the pop-up PDF
   * viewer; the backend audits the read and gates it behind `view_employee`.
   */
  async pensionDownloadUrl(employeeId: string): Promise<string> {
    const { data } = await apiClient.post<{ url: string }>(
      `/api/v1/employees/${employeeId}/pension/download/`,
    );
    return data.url;
  },

  /**
   * Sends the contract file to the API, which validates it — type, size, and
   * that the bytes match the declared format — and writes it to the store
   * before answering with the finished assignment.
   *
   * The Content-Type override is not decoration: `apiClient` defaults to
   * `application/json`, and axios reads that default *before* the body, so a
   * FormData upload with the JSON header posts the file as the string
   * "[object File]" — a 200 from a serializer that never saw a file. Axios
   * drops the header for FormData bodies, so the browser writes its own
   * boundary on the wire.
   */
  async uploadContract(
    employeeId: string,
    assignmentId: string,
    file: File,
  ): Promise<EmployeeAssignment> {
    const body = new FormData();
    body.append('file', file);

    const { data } = await apiClient.post<EmployeeAssignment>(
      `/api/v1/employees/${employeeId}/assignments/${assignmentId}/upload/`,
      body,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data;
  },

  /** Removes the stored contract, returning the assignment to "sin contrato". */
  async discardContract(employeeId: string, assignmentId: string): Promise<EmployeeAssignment> {
    const { data } = await apiClient.post<EmployeeAssignment>(
      `/api/v1/employees/${employeeId}/assignments/${assignmentId}/discard/`,
    );
    return data;
  },

  /** Resolves the short-lived signed download URL for the contract file. */
  async contractDownloadUrl(employeeId: string, assignmentId: string): Promise<string> {
    const { data } = await apiClient.post<{ url: string }>(
      `/api/v1/employees/${employeeId}/assignments/${assignmentId}/download/`,
    );
    return data.url;
  },

  /**
   * The files attached to the worker's expediente. The backend answers with the
   * active ones only unless `include_inactive=true`, and a worker without
   * `view_employee` sees only their own — the endpoint scopes itself, so this
   * needs no extra filtering.
   */
  async listFiles(employeeId: string, params: ListParams = {}): Promise<Paginated<EmployeeFile>> {
    const { data } = await apiClient.get<Paginated<EmployeeFile>>(
      `/api/v1/employees/${employeeId}/files/`,
      { params },
    );
    return data;
  },

  /**
   * Attaches a file to the worker's expediente. The backend validates MIME,
   * size and that the bytes match the declared type, then writes it to the
   * store and answers with the finished record; uploading a second file of the
   * same type retires the previous one instead of failing.
   *
   * A worker uploading their own photo only needs `employees.manage_own_profile`,
   * and the backend restricts them to the `PROFILE_PHOTO` type.
   *
   * The Content-Type override is not decoration: `apiClient` defaults to
   * `application/json`, and axios reads that default *before* the body, so a
   * FormData upload with the JSON header posts the file as the string
   * "[object File]" — a 200 from a serializer that never saw a file. Axios
   * drops the header for FormData bodies, so the browser writes its own
   * boundary on the wire.
   */
  async uploadFile(
    employeeId: string,
    fileTypeId: string,
    file: File,
    note?: string,
  ): Promise<EmployeeFile> {
    const body = new FormData();
    body.append('file_type', fileTypeId);
    body.append('file', file);
    if (note) body.append('note', note);

    const { data } = await apiClient.post<EmployeeFile>(
      `/api/v1/employees/${employeeId}/files/`,
      body,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data;
  },

  /**
   * A short-lived presigned URL to read an attached file. POST, not GET: the
   * backend mints the URL *and* audits the read, so the grant is never a
   * permanent addressable link to a private document.
   */
  async downloadFileUrl(employeeId: string, fileId: string): Promise<string> {
    const { data } = await apiClient.post<{ url: string }>(
      `/api/v1/employees/${employeeId}/files/${fileId}/download/`,
    );
    return data.url;
  },
};

/**
 * The catalog of expediente file types. Looked up by `code`, never by `name`:
 * the name is RR.HH.-editable display text and may be translated at runtime.
 *
 * `allowed_content_types` and `max_bytes` come from the `employee.file.*`
 * parameters, not from the catalog row: the backend resolves and re-validates
 * them on upload, so they are the form's hint rather than its only defence.
 */
export interface EmployeeFileType extends BaseRecord {
  code: string;
  name: string;
  description: string;
  sort_order: number;
  is_active: boolean;
  allowed_content_types: string[];
  max_bytes: number;
}

/**
 * The catalog of expediente file types. Looked up by `code`, never by `name`:
 * the name is RR.HH.-editable display text and may be translated at runtime.
 */
export const employeeFileTypesApi = createApiResource<EmployeeFileType>(
  '/api/v1/catalog/employee-file-types/',
);

export const identityDocumentTypesApi = createApiResource<IdentityDocumentType>(
  '/api/v1/catalog/identity-document-types/',
);
